import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { Observable, combineLatest, debounceTime, distinctUntilChanged, filter, map, switchMap } from 'rxjs';

import { betslipSelector } from '../../../base/store/selectors';
import { IBetslipRootState } from '../../../base/store/state';
import { BetslipUnknownPick } from '../../../core/picks/betslip-unknown-pick';
import { BetslipGeneratorContextType } from '../../bet-generation/models';
import { BetslipSummaryService } from '../../summary/services/betslip-summary.service';
import { AccaBoostContext } from '../model';
import { selectAccaBoostPicksCount, selectAccaTokenStatus, selectIsValidAccaBoostCombo } from '../selectors';
import { compareContexts } from './acca-boost.utils';

@Injectable()
export class AccaBoostContextService {
    readonly context$: Observable<AccaBoostContext>;

    constructor(store: Store<IBetslipRootState>, summaryService: BetslipSummaryService) {
        const pickCount$ = store.select(selectAccaBoostPicksCount);

        const isValidAccaBoostCombo$ = store.select(selectIsValidAccaBoostCombo);
        const accaTokenStatus$ = store.select(selectAccaTokenStatus);
        const betslipState$ = store.select(betslipSelector);

        this.context$ = combineLatest([isValidAccaBoostCombo$, accaTokenStatus$, pickCount$, betslipState$]).pipe(
            //don't take calculations until no more unknown picks are in the betslip, to make sure that delayed cds response isn't impacting accaboost notifications
            filter(([_, __, ___, betslipState]) => betslipState.picks.pickList.every((pick) => !BetslipUnknownPick.isPick(pick))),
            switchMap(([isValidAccaBoostCombo, tokenStatus, picksCount]) =>
                summaryService
                    .getSummary$(BetslipGeneratorContextType.AccaBoost)
                    .pipe(map(([, summary]) => ({ isValidAccaBoostCombo, tokenStatus, summary, picksCount }))),
            ),
            debounceTime(20),
            map((result) => {
                const { token, isOneLegAway, isSelected } = result.tokenStatus;

                const accaBoostSummary = result.summary.slipSummaries[0]?.rewardTokenModifications?.accaBoost;
                const maxReward = isSelected && (accaBoostSummary?.currentBoost.equals(accaBoostSummary.maxBoost) ?? false);

                return {
                    token,
                    isSelected: isSelected && !!accaBoostSummary,
                    isOneLegAway,
                    validCombo: result.isValidAccaBoostCombo,
                    maxReward,
                    picksCount: result.picksCount,
                };
            }),
            distinctUntilChanged(compareContexts),
        );
    }
}
